import type { AdminHostWithType } from "@/admin/hosts/hostQuery.ts";
import { filter, firstBy, groupBy, map, mapValues, sortBy, sum, sumBy, values } from "remeda";

type HostCapacity = Pick<
    AdminHostWithType,
    | "allocated_vcpu"
    | "allocated_vmem"
    | "is_accepting_workloads"
    | "is_maintenance"
    | "is_offline"
    | "usable_vcpu_amount"
    | "usable_vmem_amount"
>;

type FailureDomainHost = HostCapacity & Pick<AdminHostWithType, "location" | "pool_name" | "pool_uuid">;

export type FailureResource = "vcpu" | "vram";

export interface FailureResourceMetrics {
    tolerance: number;
    utilization: number | null;
    freePercent: number | null;
    requiredFreePercent: number | null;
    additionalFree: number | null;
}

export interface HostFailureTolerance {
    availableHosts: number;
    hosts: number;
    resources: Record<FailureResource, FailureResourceMetrics>;
}

export interface FailureDomainTolerance extends HostFailureTolerance {
    location: string;
    poolName: string;
    poolUuid: string;
    totalHosts: number;
    utilization: number | null;
}

type AvailableHostMetrics = Omit<HostFailureTolerance, "hosts">;

interface ResourceDefinition {
    getAllocated: (host: HostCapacity) => number;
    getUsable: (host: HostCapacity) => number;
}

const resourceDefinitions: Record<FailureResource, ResourceDefinition> = {
    vcpu: {
        getAllocated: (host) => host.allocated_vcpu,
        getUsable: (host) => host.usable_vcpu_amount,
    },
    vram: {
        getAllocated: (host) => host.allocated_vmem,
        getUsable: (host) => host.usable_vmem_amount,
    },
};

function isAvailable(host: HostCapacity): boolean {
    return host.is_accepting_workloads && !host.is_maintenance && !host.is_offline;
}

function resourceFailureTolerance(capacities: number[], committed: number): number {
    let spareCapacity = sum(capacities) - committed;
    let tolerance = 0;

    for (const capacity of sortBy(capacities, [(capacity) => capacity, "desc"])) {
        if (capacity > spareCapacity) break;

        spareCapacity -= capacity;
        tolerance++;
    }

    return tolerance;
}

function getResourceMetrics(
    hosts: HostCapacity[],
    getAllocated: (host: HostCapacity) => number,
    getUsable: (host: HostCapacity) => number,
): FailureResourceMetrics {
    const capacities = map(hosts, getUsable);
    const totalCapacity = sum(capacities);
    const committed = sumBy(hosts, getAllocated);
    const largestHost = firstBy(capacities, [(capacity) => capacity, "desc"]) ?? 0;
    const free = totalCapacity - committed;

    return {
        tolerance: resourceFailureTolerance(capacities, committed),
        utilization: totalCapacity > 0 ? (committed / totalCapacity) * 100 : null,
        freePercent: totalCapacity > 0 ? (free / totalCapacity) * 100 : null,
        requiredFreePercent: totalCapacity > 0 ? (largestHost / totalCapacity) * 100 : null,
        additionalFree: totalCapacity > 0 ? Math.max(largestHost - free, 0) : null,
    };
}

function getAvailableHostMetrics(hosts: Iterable<HostCapacity>): AvailableHostMetrics {
    const availableHosts = filter([...hosts], isAvailable);
    const resources: Record<FailureResource, FailureResourceMetrics> = mapValues(
        resourceDefinitions,
        ({ getAllocated, getUsable }) => getResourceMetrics(availableHosts, getAllocated, getUsable),
    );

    return {
        availableHosts: availableHosts.length,
        resources,
    };
}

function getHostFailureToleranceFromMetrics(metrics: AvailableHostMetrics): HostFailureTolerance {
    return {
        ...metrics,
        hosts: Math.min(metrics.resources.vcpu.tolerance, metrics.resources.vram.tolerance),
    };
}

/**
 * Returns how many currently available hosts may fail while guaranteeing that
 * the remaining capacity can carry all commitments assigned to available hosts.
 * The largest hosts are removed first so the result holds for any combination
 * of failed hosts.
 */
export function getHostFailureTolerance(hosts: Iterable<HostCapacity>): HostFailureTolerance {
    return getHostFailureToleranceFromMetrics(getAvailableHostMetrics(hosts));
}

/** Groups hosts by their actual failover boundary: location and hypervisor pool. */
export function getFailureDomainTolerances(hosts: Iterable<FailureDomainHost>): FailureDomainTolerance[] {
    const groupedHosts = groupBy([...hosts], (host) => `${host.location}\0${host.pool_uuid}`);

    return map(values(groupedHosts), (domainHosts) => {
        const [firstHost] = domainHosts;
        const metrics = getAvailableHostMetrics(domainHosts);
        const tolerance = getHostFailureToleranceFromMetrics(metrics);
        const utilizations = filter(
            map(values(metrics.resources), (resource) => resource.utilization),
            (utilization) => utilization != null,
        );
        const utilization = firstBy(utilizations, [(value) => value, "desc"]) ?? null;

        return {
            ...tolerance,
            location: firstHost.location,
            poolName: firstHost.pool_name,
            poolUuid: firstHost.pool_uuid,
            totalHosts: domainHosts.length,
            utilization,
        };
    });
}
